import { Controller } from "@hotwired/stimulus"
import { CUSTOM_MAP_LAYER_STYLE } from "./style_defaults"
import * as myMap from "./maps"
import { resolveMapTypeId } from "./maps"
import { mapInfoPanel } from "./map_info_panel"
import { resolveFeatureStyle } from "./feature_style"

export default class extends Controller {
  static targets = ["map", "layerData", "infoPanel", "infoPanelContent"]
  static values = {
    centerLat: { type: Number, default: 55.5 },
    centerLon: { type: Number, default: -4.6 },
    defaultZoom: { type: Number, default: 12 },
    defaultMapType: { type: String, default: 'terrain' }
  }

  async connect() {
    this.bounds = new google.maps.LatLngBounds()
    this.shapes = [] // Track all shapes with their metadata for click detection
    this.polygons = [] // Track every drawn polygon so we can clear them on redraw
    this.geojsonCache = {} // Cache fetched GeoJSON by URL to avoid duplicate requests

    // Pre-calculate bounds from layer data before creating map
    await this.precalculateBounds()

    // Initialize map with correct bounds (no animation needed)
    this.initMap()

    // Setup info panel for layer feature information
    if (this.hasInfoPanelTarget && this.hasInfoPanelContentTarget) {
      mapInfoPanel.setup(this, this.infoPanelTarget, this.infoPanelContentTarget, this.map)
    }

    // Load and draw layer
    await this.loadLayer()
  }

  closeInfoPanel() {
    mapInfoPanel.close()
  }

  async precalculateBounds() {
    if (!this.hasLayerDataTarget || !this.layerDataTarget.textContent.trim()) {
      return
    }

    const layerData = JSON.parse(this.layerDataTarget.textContent)
    let geojson

    if (layerData.geojson_url) {
      try {
        const response = await fetch(layerData.geojson_url)
        if (response.ok) {
          geojson = await response.json()
          this.geojsonCache[layerData.geojson_url] = geojson
        }
      } catch (error) {
        // Silently handle fetch errors
      }
    } else if (layerData.geojson) {
      geojson = layerData.geojson
    }

    if (geojson && geojson.features) {
      geojson.features.forEach(feature => {
        this.extractBoundsFromFeature(feature)
      })
    }
  }

  extractBoundsFromFeature(feature) {
    const geometry = feature.geometry

    if (geometry.type === 'Polygon') {
      geometry.coordinates[0].forEach(coord => {
        this.bounds.extend({ lat: coord[1], lng: coord[0] })
      })
    } else if (geometry.type === 'MultiPolygon') {
      geometry.coordinates.forEach(polygon => {
        polygon[0].forEach(coord => {
          this.bounds.extend({ lat: coord[1], lng: coord[0] })
        })
      })
    }
  }

  initMap() {
    const mapType = resolveMapTypeId(this.defaultMapTypeValue)
    let mapOptions
    if (!this.bounds.isEmpty()) {
      // Create map centered on the bounds
      const center = this.bounds.getCenter()
      mapOptions = myMap.defaultMapOptions(center.lat(), center.lng(), this.defaultZoomValue, mapType)
    } else {
      // Fallback to default center if no bounds
      mapOptions = myMap.defaultMapOptions(this.centerLatValue, this.centerLonValue, 8, mapType)
    }

    this.map = new google.maps.Map(this.mapTarget, mapOptions)

    // Fit to bounds immediately (no animation since map just created)
    if (!this.bounds.isEmpty()) {
      this.map.fitBounds(this.bounds)

      // Limit zoom
      google.maps.event.addListenerOnce(this.map, 'idle', () => {
        if (this.map.getZoom() > 15) {
          this.map.setZoom(15)
        }
      })
    }
  }

  async loadLayer() {
    if (!this.hasLayerDataTarget || !this.layerDataTarget.textContent.trim()) {
      return
    }

    const layerData = JSON.parse(this.layerDataTarget.textContent)

    let geojson

    if (layerData.geojson_url) {
      try {
        if (this.geojsonCache[layerData.geojson_url]) {
          geojson = this.geojsonCache[layerData.geojson_url]
        } else {
          const response = await fetch(layerData.geojson_url)
          if (!response.ok) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`)
          }
          geojson = await response.json()
          this.geojsonCache[layerData.geojson_url] = geojson
        }
      } catch (error) {
        return
      }
    }
    else if (layerData.geojson) {
      geojson = layerData.geojson
    }
    else {
      return
    }

    // Draw features (bounds already calculated)
    geojson.features.forEach(feature => {
      this.drawFeature(feature, layerData)
    })
  }

  drawFeature(feature, layerData) {
    const geometry = feature.geometry
    const properties = feature.properties || {}
    const { shapeStyle, metadata } = resolveFeatureStyle(layerData, properties)

    if (geometry.type === 'Polygon') {
      this.drawPolygon(geometry.coordinates, shapeStyle, metadata)
    } else if (geometry.type === 'MultiPolygon') {
      geometry.coordinates.forEach(polygonCoords => {
        this.drawPolygon(polygonCoords, shapeStyle, metadata)
      })
    }
  }

  drawPolygon(coordinates, shapeStyle, metadata) {
    const paths = coordinates.map(ring => {
      return ring.map(coord => {
        const latlng = { lat: coord[1], lng: coord[0] }
        this.bounds.extend(latlng)
        return latlng
      })
    })

    const polygon = new google.maps.Polygon({
      paths: paths,
      strokeColor: shapeStyle.strokeColor,
      fillColor: shapeStyle.fillColor,
      fillOpacity: shapeStyle.fillOpacity,
      strokeWeight: shapeStyle.strokeWeight,
      strokeOpacity: shapeStyle.strokeOpacity,
      map: this.map
    })

    this.polygons.push(polygon)

    // Store shape with metadata for click detection
    if (metadata.featureName || metadata.description) {
      this.shapes.push({ shape: polygon, type: 'polygon', metadata })
      polygon.addListener('click', (event) => this.handleShapeClick(event))
    }
  }

// redraw is used when editing custom map layers
  // TODO: refactor draw/redraw to use google.maps.Data layer — would make refresh a
  // one-liner (forEach remove + addGeoJson) and remove the need to track polygons
  // manually. Stacked-click hit-testing still needs manual iteration since Data only
  // fires click on the topmost feature.
  redrawLayer(event) {
    if (!this.map) return

    // Clear existing polygons (and their click metadata)
    this.polygons.forEach(p => p.setMap(null))
    this.polygons = []
    this.shapes = []

    // Prefer geojson from the dispatched event; fall back to layerData
    const layerData = this.hasLayerDataTarget && this.layerDataTarget.textContent.trim()
      ? JSON.parse(this.layerDataTarget.textContent)
      : {}
    const geojson = event?.detail?.geojson
    if (!geojson || !geojson.features) return

    geojson.features.forEach(feature => {
      this.drawFeature(feature, layerData)
    })
  }

  handleShapeClick(event) {
    const clickLatLng = event.latLng
    const featuresAtPoint = []

    // Find all shapes that contain the click point
    this.shapes.forEach(({ shape, type, metadata }) => {
      let containsPoint = false

      if (type === 'polygon') {
        containsPoint = google.maps.geometry.poly.containsLocation(clickLatLng, shape)
      }

      if (containsPoint) {
        featuresAtPoint.push(metadata)
      }
    })

    if (featuresAtPoint.length > 0) {
      const customEvent = new CustomEvent('map-features-clicked', {
        detail: {
          features: featuresAtPoint,
          position: { lat: clickLatLng.lat(), lng: clickLatLng.lng() }
        },
        bubbles: true
      })
      document.dispatchEvent(customEvent)
    }
  }
}
